import { type ReactNode } from "react";
import { useFieldContext } from "./form-context";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { CircleAlert, Redo2 } from "lucide-react";
import { Button } from "../ui/button";
import { cn, sanitizeHtml } from "@/lib/utils";
import { OverridesButton } from "../overrides-button";

export function FormSelect({
  label,
  id,
  description,
  placeholder,
  options,
  htmlContent,
  error,
  onChange,
  defaultSystemValue,
  hasOverriddenValue,
  openOverridesModal,
}: {
  label: ReactNode;
  id: string;
  description?: string;
  placeholder: string;
  options: { value: string | number; label: string }[];
  htmlContent?: string;
  error?: string;
  onChange?: (value: string | number) => void;
  defaultSystemValue?: string;
  isSystemSettings?: boolean;
  openOverridesModal?: () => void;
  hasOverriddenValue?: boolean;
}) {
  const field = useFieldContext<string | number>();
  const value = field?.state.value ?? "";
  const sanitizedHtml = htmlContent ? sanitizeHtml(htmlContent) : null;

  const defaultSystemLabel =
    options.find((opt) => opt.value.toString() === defaultSystemValue)?.label ?? defaultSystemValue;

  // The convertion in the select below is due to limitations in the radix ui library; it expects string values
  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center gap-2">
        {label && <div className="text-sm leading-none font-medium">{label}</div>}
        {defaultSystemValue !== undefined && (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleAlert
                className={cn(
                  "w-4 h-4 text-orange-500",
                  value === defaultSystemValue && "invisible pointer-events-none"
                )}
              />
            </TooltipTrigger>
            <TooltipContent>
              <p>This value overwrites the default system value: {defaultSystemLabel}</p>
            </TooltipContent>
          </Tooltip>
        )}
        {defaultSystemValue !== undefined && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "text-orange-500 pb-0 pt-0 mr-auto",
                  value === defaultSystemValue && "invisible pointer-events-none"
                )}
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  field?.setValue(defaultSystemValue ?? "");
                }}
              >
                <Redo2 className="w-2 h-2" />
                Reset to default
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Reset the value to the default system value: {defaultSystemLabel}</p>
            </TooltipContent>
          </Tooltip>
        )}
        {hasOverriddenValue && <OverridesButton onClick={openOverridesModal!} />}
      </div>
      <div className="flex gap-2">
        <Select
          value={
            field.state.value != null &&
            field.state.value !== "" &&
            options.some((opt) => opt.value.toString() === field.state.value?.toString())
              ? field.state.value.toString()
              : undefined
          }
          onValueChange={(value) => {
            const option = options.find((opt) => opt.value.toString() === value);
            if (option) {
              field.handleChange(option.value);
              onChange?.(option.value);
            }
          }}
        >
          <SelectTrigger id={id} className="w-full">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value.toString()}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      {description && <div className="text-xs form-description">{description}</div>}
      {htmlContent && (
        <div className="text-xs form-description" dangerouslySetInnerHTML={{ __html: sanitizedHtml || "" }} />
      )}
      {error && <div className="text-sm text-red-500">{error}</div>}
    </div>
  );
}
